import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { API_BASE_URL } from '../../config';
import axiosInstance from '../../services/axiosInstance';
import {
  IAssetReferenceValue,
  IReferenceFieldConfig,
} from '../../types/damTypes';
import { normalizeAssetReferenceValues } from './utils';

type ReferenceSearchOption = {
  assetId: string;
  label: string;
  subtitle?: string;
  previewUrl?: string;
  fileName?: string;
};

interface AssetReferenceFieldInputProps {
  label: string;
  placeholder?: string;
  helperText?: string;
  value: IAssetReferenceValue[];
  onChange: (value: IAssetReferenceValue[]) => void;
  disabled?: boolean;
  required?: boolean;
  config?: IReferenceFieldConfig;
}

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;

function toAssetPreviewUrl(previewUrl?: string): string | undefined {
  if (!previewUrl) return undefined;
  if (/^https?:\/\//i.test(previewUrl)) return previewUrl;
  return `${API_BASE_URL}${previewUrl}`;
}

function normalizeOption(option: ReferenceSearchOption): ReferenceSearchOption {
  return {
    assetId: String(option.assetId || '').trim(),
    label: String(option.label || '').trim(),
    subtitle: String(option.subtitle || '').trim() || undefined,
    previewUrl: option.previewUrl,
    fileName: option.fileName,
  };
}

const AssetReferenceFieldInput: React.FC<AssetReferenceFieldInputProps> = ({
  label,
  placeholder,
  helperText,
  value,
  onChange,
  disabled = false,
  required = false,
  config,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ReferenceSearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const normalizedValue = useMemo(
    () => normalizeAssetReferenceValues(value),
    [value],
  );

  const selectedOptions = useMemo(() => {
    const optionsById = new Map<string, ReferenceSearchOption>();
    options.forEach((option) => {
      optionsById.set(option.assetId, normalizeOption(option));
    });

    return normalizedValue.map((item) => {
      const matchingOption = optionsById.get(item.assetId);
      if (matchingOption) return matchingOption;

      return {
        assetId: item.assetId,
        label: item.labelSnapshot,
        subtitle: item.subtitleSnapshot,
      } as ReferenceSearchOption;
    });
  }, [normalizedValue, options]);

  const clearOptions = () => {
    setOptions((previousOptions) => (previousOptions.length > 0 ? [] : previousOptions));
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (disabled) {
      setLoading(false);
      clearOptions();
      return;
    }

    const normalizedQuery = inputValue.trim();
    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setLoading(false);
      clearOptions();
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/api/reference-fields/assets/search', {
          params: {
            q: normalizedQuery,
            limit: 20,
            ...(config?.categoryId ? { categoryId: config.categoryId } : {}),
            ...(config?.searchFields?.length ? { searchFields: config.searchFields.join(',') } : {}),
            ...(config?.displayField ? { displayField: config.displayField } : {}),
            ...(config?.secondaryDisplayField ? { secondaryDisplayField: config.secondaryDisplayField } : {}),
          },
        });

        const nextOptions = Array.isArray(response.data)
          ? response.data.map((option: any) => normalizeOption(option))
          : [];
        setOptions(nextOptions.filter((option) => option.assetId && option.label));
      } catch (error) {
        console.error('Failed to search reference field assets:', error);
        clearOptions();
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [
    config?.categoryId,
    config?.displayField,
    config?.secondaryDisplayField,
    config?.searchFields,
    disabled,
    inputValue,
  ]);

  return (
    <Autocomplete
      multiple
      clearOnBlur={false}
      options={options}
      value={selectedOptions}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(newInputValue);
        }
      }}
      onChange={(_, newValue) => {
        const seen = new Set<string>();
        const nextValue = newValue
          .map((option) => normalizeOption(option))
          .filter((option) => {
            if (!option.assetId || seen.has(option.assetId)) return false;
            seen.add(option.assetId);
            return true;
          })
          .map((option) => ({
            assetId: option.assetId,
            labelSnapshot: option.label,
            ...(option.subtitle ? { subtitleSnapshot: option.subtitle } : {}),
          }));

        onChange(nextValue);
        setInputValue('');
      }}
      filterOptions={(currentOptions) => currentOptions}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option) => option.label || option.fileName || option.assetId}
      isOptionEqualToValue={(option, selectedOption) => option.assetId === selectedOption.assetId}
      noOptionsText={
        inputValue.trim().length < MIN_QUERY_LENGTH
          ? 'Skriv minst 2 tecken för att söka'
          : 'Inga träffar'
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option.label}
            {...getTagProps({ index })}
            key={`${option.assetId}-${index}`}
            size="small"
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.assetId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar
            variant="rounded"
            src={toAssetPreviewUrl(option.previewUrl)}
            sx={{ width: 36, height: 36, bgcolor: 'grey.100', color: 'text.secondary' }}
          >
            {option.label?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {option.label}
            </Typography>
            {option.subtitle && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || 'Sök och välj referenser'}
          helperText={helperText}
          required={required}
          error={required && normalizedValue.length === 0}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AssetReferenceFieldInput;
